export const HOURS_PER_DAY = 24;

export const formatValidityHours = (value: unknown): string | null => {
  const validityHours = Number(value ?? 0);
  if (validityHours <= 0) return null;
  return validityHours % HOURS_PER_DAY === 0
    ? `${validityHours / HOURS_PER_DAY}d`
    : `${validityHours}h`;
};

/**
 * Format a remaining-time hour count for rider-facing surfaces.
 *
 * Rules (see GAU-51):
 *  - `>= 24h`: `Xd Yh`, dropping ` Yh` when Y === 0 (48h -> "2d", 30h -> "1d 6h").
 *  - `1h <= x < 24h`: `Xh` (rounded to the nearest whole hour).
 *  - `0 < x < 1h`: `<1h` (some time left but less than an hour).
 *  - `<= 0` / `null` / `undefined`: `0h`. Callers that need to render
 *    "Expired" / "Consumed" should derive that from the pack status, not from
 *    this formatter.
 */
export const formatRemainingHours = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '0h';
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) return '0h';

  // Sub-hour tier: explicit "<1h" so riders can tell something is left vs 0.
  if (hours < 1) return '<1h';

  // Round to nearest whole hour BEFORE splitting into days + remainder so
  // values like 47.6h render as "2d" rather than "1d 24h". Floor-then-round
  // can roll the remainder up to 24 and produce an invalid label.
  const rounded = Math.round(hours);
  if (rounded < HOURS_PER_DAY) return `${rounded}h`;

  const days = Math.floor(rounded / HOURS_PER_DAY);
  const remainder = rounded - days * HOURS_PER_DAY;
  return remainder === 0 ? `${days}d` : `${days}d ${remainder}h`;
};
